/**
 * Verification record viewer — the drill-down behind the per-document
 * verification chip on the Requirements & Design card.
 *
 * A VIEWER only: reads one verificationRuns record (written by the
 * post-generation runner, admin-SDK only) and renders it inline within the
 * card, per the EDC-card inline rule — never a modal, never pushing the
 * page. See docs/post-generation-verification-scope.md §4.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { X, Loader2 } from 'lucide-react';

/* Plain-language labels for check ids (no raw ids as primary labels). */
const CHECK_LABELS: Record<string, string> = {
  V1: 'Requirement links resolve',
  V2: 'Links stay within what each section was given',
  V3: 'Sections that link to nothing',
  R1: 'Stored content matches this generation exactly',
  FT1: 'Reverse links point at real content',
  FT2: 'Forward and reverse links agree',
  V5: 'No content dropped during rendering',
  T1: 'Typical element links to a requirement',
  INV1: 'Specifics that have no source',
  INV2: 'Names that may not be in the data dictionary',
};

const STATUS_STYLES: Record<string, string> = {
  PASS: 'bg-green-50 text-green-700 border-green-200',
  FINDINGS: 'bg-amber-50 text-amber-700 border-amber-200',
  FAIL: 'bg-red-50 text-red-700 border-red-200',
  SKIPPED: 'bg-gray-50 text-gray-500 border-gray-200',
};

const STATUS_WORDS: Record<string, string> = {
  PASS: 'Pass',
  FINDINGS: 'Findings',
  FAIL: 'Failed',
  SKIPPED: 'Not checked',
};

/* Correction disclosure (display only — approve/decline lives in the EDC
 * Build page's Needs review card, never here; one action surface per item). */
const CORRECTION_PILLS: Record<string, { word: string; cls: string }> = {
  applied: { word: 'Corrected automatically', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  offered: { word: 'Needs review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  would_apply: { word: 'Correction identified (not applied)', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  deferred: { word: 'Correction deferred', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  failed: { word: 'Correction did not verify', cls: 'bg-red-50 text-red-700 border-red-200' },
};

function CorrectionNote({ correction }: { correction: any }) {
  const pill = CORRECTION_PILLS[correction?.status];
  if (!pill) return null;
  const detail =
    correction.status === 'deferred'
      ? correction.deferralReason
      : correction.status === 'failed'
        ? correction.reason
        : correction.status === 'offered'
          ? 'Approve or decline it in the Needs review card.'
          : null;
  return (
    <div className="mt-0.5 ml-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${pill.cls}`}
        >
          {pill.word}
        </span>
        {detail && <span className="text-[11px] text-gray-500">{detail}</span>}
        <span className="text-[10px] text-gray-400">{correction.correctionId}</span>
      </div>
      {Array.isArray(correction.also) &&
        correction.also.map((a: any) => {
          const p = CORRECTION_PILLS[a?.status];
          if (!p) return null;
          return (
            <div key={a.correctionId} className="flex items-center gap-2 mt-0.5">
              <span
                className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${p.cls}`}
              >
                {p.word}
              </span>
              <span className="text-[10px] text-gray-400">{a.correctionId}</span>
            </div>
          );
        })}
    </div>
  );
}

interface VerificationPanelProps {
  /** Firestore path of the verified document (e.g. projects/x/requirements/sds). */
  docPath: string;
  runId: string;
  onClose: () => void;
}

export default function VerificationPanel({
  docPath,
  runId,
  onClose,
}: VerificationPanelProps) {
  const [record, setRecord] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const segments = `${docPath}/verificationRuns/${runId}`.split('/');
    const ref = doc(db, segments[0], ...segments.slice(1));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setRecord(snap.exists() ? snap.data() : null);
        if (!snap.exists()) setLoadError('Verification record not found.');
      },
      (err) => {
        console.error('[verification] record subscription error:', err);
        setLoadError('Could not load the verification record.');
      }
    );
    return () => unsub();
  }, [docPath, runId]);

  const completedAt = record?.completedAt?.toDate?.();

  return (
    <div className="mt-2 border-t border-gray-200 bg-gray-50/50 rounded-b-lg animate-fade-in">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="text-xs font-semibold text-gray-700">
          Generation check
          {completedAt && (
            <span className="font-normal text-gray-500">
              {' '}
              &middot; {completedAt.toLocaleString()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 text-gray-500"
          title="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto px-3 pb-3 space-y-3">
        {!record && !loadError && (
          <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading&hellip;
          </div>
        )}
        {loadError && <p className="text-xs text-red-600">{loadError}</p>}

        {record?.status === 'error' && (
          <p className="text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded px-2 py-1.5">
            The check itself could not run
            {record.error ? `: ${record.error}` : '.'} The generated document is
            unaffected.
          </p>
        )}

        {Array.isArray(record?.checks) && (
          <div className="space-y-1">
            {record.checks.map((c: any) => (
              <div key={c.checkId} className="text-xs">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${STATUS_STYLES[c.status] || STATUS_STYLES.SKIPPED}`}
                  >
                    {STATUS_WORDS[c.status] || c.status}
                  </span>
                  <span className="font-medium text-gray-800">
                    {CHECK_LABELS[c.checkId] || c.checkId}
                  </span>
                </div>
                {c.status !== 'PASS' && (
                  <p className="text-gray-600 mt-0.5 ml-1">{c.detail}</p>
                )}
                {c.correction && <CorrectionNote correction={c.correction} />}
                {Array.isArray(c.items) && c.items.length > 0 && c.status !== 'PASS' && (
                  <ul className="mt-0.5 ml-3 list-disc list-inside text-gray-500">
                    {c.items.slice(0, 5).map((it: any) => (
                      <li key={it.subject + it.fingerprint}>
                        <span className="font-medium">{it.subject}</span>: {it.detail}
                      </li>
                    ))}
                    {c.items.length > 5 && (
                      <li className="list-none text-gray-400">
                        &hellip;and {c.items.length - 5} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {Array.isArray(record?.measurements?.perSection) &&
          record.measurements.perSection.length > 0 && (
            <div className="overflow-x-auto">
              <table className="text-[11px] text-gray-600 w-full">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="pr-3 py-0.5 font-medium">Section</th>
                    <th className="pr-3 py-0.5 font-medium text-right">Elements</th>
                    <th className="pr-3 py-0.5 font-medium text-right">Given</th>
                    <th className="pr-3 py-0.5 font-medium text-right">Median links</th>
                    <th className="pr-3 py-0.5 font-medium text-right">Unlinked %</th>
                  </tr>
                </thead>
                <tbody>
                  {record.measurements.perSection.map((s: any) => (
                    <tr key={s.sectionId} className="border-t border-gray-100">
                      <td className="pr-3 py-0.5">{s.sectionId}</td>
                      <td className="pr-3 py-0.5 text-right">{s.elementCount}</td>
                      <td className="pr-3 py-0.5 text-right">{s.suppliedCount ?? '—'}</td>
                      <td className="pr-3 py-0.5 text-right">{s.median}</td>
                      <td className="pr-3 py-0.5 text-right">{s.zeroPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {record && (
          <p className="text-[10px] text-gray-400 border-t border-gray-100 pt-1.5">
            Run {record.runId}
            {record.runSeq > 1 &&
              ` — earlier runs are retained with the document`}
            {record.generator?.commit &&
              ` · build ${String(record.generator.commit).slice(0, 8)}`}
            {record.generator?.kRevision && ` · ${record.generator.kRevision}`}
          </p>
        )}
      </div>
    </div>
  );
}
